/**
 * K12Net SSO yapılandırması — tek kaynak.
 *
 * Hem K12NetRedirect.vue (authorize adımı) hem K12Net.vue (dönüş adımı) buradan okur.
 * Eskiden `redirect_uri` iki dosyada ayrı ayrı yazılıydı; OAuth'ta authorize ve token
 * adımlarındaki bu değerin birebir aynı olması zorunlu olduğu için yerel akış sürekli
 * bozuluyordu. Platform listesi de üç yerde (logo şablonu, örnek URL yorumu, secret key
 * bloğu) tutulduğu için tutarsızdı: `ell` için logo vardı ama anahtar yoktu,
 * `sensation-english` / `sensations-english` yazımı ayrışmıştı.
 */

const isLocal = window.location.hostname === 'localhost';

// serviceApiBaseUrl artık Gradingly.vue de kullandığı için genel dosyaya taşındı.
export { serviceApiBaseUrl } from './serviceApi';

export const K12NET = {
  authorizeUrl: 'https://api.k12net.com/GWCore.Web/connect/authorize',
  clientId: '09150701-5163-4a58-b2a5-b859c20ed848',
  // Test ortamı:
  // authorizeUrl: 'https://api-test.k12net.com/GWCore.Web/connect/authorize',
  // clientId: 'b0324b63-33ac-42ae-bbfd-129c18008764',
};

/**
 * K12Net'e bildirilen dönüş adresi. authorize ve token adımlarında aynı olmak zorunda.
 */
export function k12netRedirectUri() {
  return isLocal
    ? 'https://localhost:8080/#/k12net'
    : 'https://service.ueslms.com/#/k12net';
}

// Platform payload'ını şifrelemek için kullanılan AES anahtarları.
// UYARI: bu anahtarlar derlenmiş bundle'da açıktır ve hedef platform bileşenleri
// (ör. Kidzwonder.vue) payload'ı zaten tarayıcıda aynı anahtarla çözer. Yani anahtarı
// buradan kaldırmak tek başına bir şey kazandırmaz; kalıcı çözüm payload yerine tek
// kullanımlık ticket'a geçmek ve bu tüm platformları ilgilendiren ayrı bir iştir.
// Bkz. ueslms/.claude/notes/k12net-entegrasyonu.md — "Hedef mimari".
const KEY_UES = 'HJx7t2KxTCHYvtzB4w7AQhVRaf7bTm9WWFQpBMiR@j7Gpgw8';
const KEY_ELL = 'Pbt6DND9k5tu7Tdj284-1szLNPEXwcVznFMN4G@UdQtZ7.GEU7!BPy';
const KEY_RAZ = 'DZYQSbQQKmruR4ZYOLD%s7163BZjoyViqF7gWy.A6Gd9JAyXCL!eNGsrEJVcPUe';
const KEY_RLP = 'VswFYGzh2k45N7Q8R9@TBuCVexFY.G2J3K4N6p7Q%9SATB-VdWeXFZHJ3m5N';
const KEY_EPB = 'fH9G4bgFyZu6URVUprrqp!Bxjuz9Ajj6U7E7fP9_F!u3TkXnjVxZZkNQ-CaxLD2p';
const KEY_MEA = 'zHjnVhHnT.D_8@bp29QZKeLr6VfePjrm.8msWgzR9hU.CbJUcEVhQuJ6NTJtQJCE';
const KEY_RST = 'Ke8bg7uCBhGxC2rGpiKBfJ@CvMCTwy@gyCUxyFjV4pReKz!RdgPzisjDxPJw7-zU';

// Aşağıdaki anahtarlar, ilgili platform bileşeninin `?sso=` parametresini çözerken
// kullandığı `secretKey` ile birebir aynıdır (bkz. Hexagonia.vue, LanguageMate.vue,
// Gradingly.vue, BoostifyEdu.vue, KidzwonderJr.vue, Kidzwonder3D.vue,
// Espresso.vue). K12Net.vue payload'ı bu anahtarla şifreliyor, hedef bileşen aynı
// anahtarla çözüyor — uyuşmazsa yönlendirme sessizce boş sayfaya düşer.
// Hexagonia, Deutschbesten ve MyEduClass zaten KEY_UES kullanıyor; LGS XXLarge ise
// KEY_RLP ile aynı anahtarı kullandığı için ayrı sabit tanımlanmadı.
const KEY_LMATE = 'Y11SwDifZUIu7.iQboREQwiVQzC_0k1KvBkFeCr5nK9K!e9Hr8XvscKmCK3PVR4zw';
const KEY_GRAD  = '2716c9%bc44dda8829.2022a48841eb34!4cc301b23+f79b7a5f5-8f7f4741a!';
const KEY_BEDU  = 'T6VCnkxXEx8ixtHxpteZnbm0YF9KwvLU0iBTOf1vJz1Y3fzZ';
const KEY_KWJR  = 'CvKOo-HAVMfTgYeBEDSZWjyRf59rp-z3a3yXajQb5RGI71YS86fm2nlUrk6ZtWY2TzWO1eYlyADfuUP1NYtepg';
const KEY_KW3D  = 'xnD1LuiJpRenBZOCNk_Ziws4wm3Ci3q1E3EzMuxzUOBT-HrA8tL-fOSHaIySKdh8mUMxfMUvd-tQPCrybPwIzg';
const KEY_ESP   = 'NJjNop!B2LJbfgcswaiwdVheMG-hLnx3WD!ibTLVpPNqeCYn7-TiCtHjFDkR.BCy';

const base = 'https://service.ueslms.com/#/';

/**
 * Desteklenen platformlar. Anahtar, K12Net'e `state` olarak gönderilen değerdir
 * (`/#/k12net-redirect/?platform=<anahtar>`).
 *
 * NOT: `ell` bilinçli olarak listede yok. Eski kodda logosu ve örnek URL'i vardı ama
 * secret key / yönlendirme adresi hiç tanımlanmamıştı; o platformla gelen kullanıcı
 * `location.href = "null…"` ile sessizce boş sayfaya düşüyordu. ELL ürünleri
 * `efs-plus` ve `english-academe-jr` anahtarlarıyla geliyor. `ell` gerçekten
 * kullanılıyorsa doğru anahtar/adres öğrenilip buraya eklenmeli.
 */
export const K12NET_PLATFORMS = {
  'kidzwonder':         { logo: 'kidzwonder.png',         secretKey: KEY_UES, redirectUrl: base + 'kidzwonder/?sso=' },
  'uesportals':         { logo: 'ues-portals.png',        secretKey: KEY_UES, redirectUrl: base + 'uesportals/?sso=' },
  'myeduclass':         { logo: 'myeduclass.png',         secretKey: KEY_UES, redirectUrl: base + 'myeduclass/?sso=' },
  'highlights-library': { logo: 'highlights-library.png', secretKey: KEY_UES, redirectUrl: base + 'hll/?sso=' },
  'efs-plus':           { logo: 'efs-plus.png',           secretKey: KEY_ELL, redirectUrl: base + 'efs/?sso=' },
  'english-academe-jr': { logo: 'english-academe-jr.png', secretKey: KEY_ELL, redirectUrl: base + 'efs/?sso=' },
  // Logo dosyasının adı tekil ("sensation"), platform anahtarı çoğul ("sensations").
  'sensations-english': { logo: 'sensation-english.png',  secretKey: KEY_ELL, redirectUrl: base + 'sensations/?sso=' },
  'news-o-matic':       { logo: 'news-o-matic.png',       secretKey: KEY_ELL, redirectUrl: base + 'nom/?sso=' },
  'raz-plus':           { logo: 'raz-plus.png',           secretKey: KEY_RAZ, redirectUrl: base + 'raz-plus/?sso=' },
  'richmondlp':         { logo: 'richmondlp.png',         secretKey: KEY_RLP, redirectUrl: base + 'rlp/?sso=' },
  'englishplaybox':     { logo: 'englishplaybox.png',     secretKey: KEY_EPB, redirectUrl: base + 'englishplaybox/?sso=' },
  'myeduassessment':    { logo: 'myeduassessment.png',    secretKey: KEY_MEA,   redirectUrl: base + 'myeduassessment/?sso=' },
  'richmond-studio':    { logo: 'richmond-studio.png',    secretKey: KEY_RST,   redirectUrl: base + 'richmondstudio/?sso=' },
  // 03.09.2026 — geri kalan `?sso=` alan platformlar da eklendi. Hariç tutulanlar:
  // fnlenglish, doga, maneno, sebitvcloud, sinavreadingmachine, speakia, maya.
  'hexagonia':          { logo: 'hexagonia.png',          secretKey: KEY_UES,   redirectUrl: base + 'hexagonia/?sso=' },
  'deutschbesten':      { logo: 'deutschbesten.png',      secretKey: KEY_UES,   redirectUrl: base + 'deutschbesten/?sso=' },
  'languagemate':       { logo: 'languagemate.png',       secretKey: KEY_LMATE, redirectUrl: base + 'languagemate/?sso=' },
  'lgsxxlarge':         { logo: 'lgsxxl-logo.png',        secretKey: KEY_RLP,   redirectUrl: base + 'lgsxxlarge/?sso=' },
  'gradingly':          { logo: 'gradingly.png',          secretKey: KEY_GRAD,  redirectUrl: base + 'gradingly/?sso=' },
  'boostifyedu':        { logo: 'boostifyedu.png',        secretKey: KEY_BEDU,  redirectUrl: base + 'boostifyedu/?sso=' },
  'kidzwonder-jr':      { logo: 'kidzwonder-jr.png',      secretKey: KEY_KWJR,  redirectUrl: base + 'kidzwonder-jr/?sso=' },
  'kidzwonder-3d':      { logo: 'kidzwonder-3d.png',      secretKey: KEY_KW3D,  redirectUrl: base + 'kidzwonder-3d/?sso=' },
  'espresso':           { logo: 'espresso.png',           secretKey: KEY_ESP,   redirectUrl: base + 'espresso/?sso=' },
};
